<script setup lang="ts">
/**
 * MemberEditDrawer — slide-in drawer for editing a board member's role,
 * active status, and notification preferences.
 *
 * Per spec §7, notification preference _editing_ belongs to the Settings slice.
 * This drawer shows the current prefs read-only and allows role + isActive changes.
 */
import { ref, watch } from 'vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useBoardMembersStore } from '@/stores/boardMembers';
import type { MemberListItemDto } from '@/stores/boardMembers';
import type { BoardRole } from '@/api/boardTypes';

interface Props {
  member: MemberListItemDto;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  updated: [];
}>();

const store = useBoardMembersStore();

const role = ref<BoardRole>(props.member.role);
const isActive = ref(props.member.isActive);
const isSubmitting = ref(false);
const submitError = ref<string | null>(null);

// Reset form when member prop changes
watch(() => props.member, (m) => {
  role.value = m.role;
  isActive.value = m.isActive;
});

const ROLES: { value: BoardRole; label: string }[] = [
  { value: 'BoardMember', label: 'Board Member' },
  { value: 'President', label: 'President' },
  { value: 'Treasurer', label: 'Treasurer' },
  { value: 'Secretary', label: 'Secretary' },
  { value: 'MaintenanceChair', label: 'Maintenance Chair' },
  { value: 'PropertyManager', label: 'Property Manager' },
];

async function save() {
  isSubmitting.value = true;
  submitError.value = null;
  try {
    await store.updateMember(props.member.id, {
      role: role.value,
      isActive: isActive.value,
    });
    emit('updated');
    emit('close');
  } catch (e) {
    submitError.value = e instanceof Error ? e.message : 'Update failed.';
  } finally {
    isSubmitting.value = false;
  }
}

function notificationMethods(): string[] {
  if (!props.member.notificationMethodsJson) return [];
  try {
    return JSON.parse(props.member.notificationMethodsJson) as string[];
  } catch { return []; }
}
</script>

<template>
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-40 flex justify-end"
    @click.self="emit('close')"
  >
    <div class="absolute inset-0 bg-fg/20" @click="emit('close')" />

    <!-- Drawer panel -->
    <div
      class="relative z-50 w-full max-w-sm bg-surface shadow-lg flex flex-col h-full"
      role="dialog"
      aria-label="Edit board member"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-border">
        <div class="text-card text-fg">Edit member</div>
        <button
          class="w-8 h-8 grid place-items-center text-muted hover:text-fg hover:bg-canvas-soft rounded-md transition-colors"
          aria-label="Close"
          @click="emit('close')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
        <!-- Member info (read-only) -->
        <div class="flex items-center gap-3 p-3 bg-canvas-soft rounded-lg">
          <div
            class="w-9 h-9 rounded-pill bg-navy-100 text-navy-700 grid place-items-center font-semibold text-caption flex-shrink-0"
          >
            {{ member.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() }}
          </div>
          <div class="min-w-0">
            <div class="text-body font-semibold text-fg truncate">{{ member.name }}</div>
            <div class="text-caption text-muted font-mono tabular-nums truncate">{{ member.email }}</div>
          </div>
        </div>

        <!-- Role -->
        <label class="flex flex-col gap-1.5">
          <span class="text-caption font-semibold text-fg-soft uppercase tracking-wide">Role</span>
          <select
            v-model="role"
            class="h-9 px-3 text-body border border-border rounded-md bg-surface focus:outline-none focus:ring-2 focus:ring-navy-500"
          >
            <option v-for="r in ROLES" :key="r.value" :value="r.value">{{ r.label }}</option>
          </select>
        </label>

        <!-- Active status -->
        <label class="flex items-center gap-2.5 cursor-pointer">
          <input
            v-model="isActive"
            type="checkbox"
            class="w-4 h-4 accent-navy-700"
          >
          <span class="text-body text-fg-soft">Member is active</span>
        </label>

        <!-- Notification prefs (read-only per spec §7 — Settings slice owns editing) -->
        <div class="flex flex-col gap-1.5">
          <span class="text-caption font-semibold text-fg-soft uppercase tracking-wide">
            Notification methods
            <span class="text-muted normal-case font-normal ml-1">(edit in Settings)</span>
          </span>
          <div class="flex gap-2">
            <span
              v-for="method in ['email', 'sms', 'push']"
              :key="method"
              :class="[
                'px-2.5 h-7 text-caption rounded-pill border font-medium capitalize',
                notificationMethods().includes(method)
                  ? 'bg-green-100 text-green-700 border-green-100'
                  : 'bg-canvas-soft text-muted border-border',
              ]"
              class="inline-flex items-center"
            >{{ method }}</span>
          </div>
        </div>

        <!-- Error -->
        <p v-if="submitError" class="text-caption text-red-700">{{ submitError }}</p>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between gap-2 px-5 py-4 border-t border-border">
        <BaseButton
          variant="danger"
          size="md"
          :loading="isSubmitting"
          :disabled="!isActive"
          @click="isActive = false; save()"
        >
          Deactivate
        </BaseButton>
        <div class="flex gap-2">
          <BaseButton variant="ghost" size="md" @click="emit('close')">Cancel</BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            :loading="isSubmitting"
            @click="save"
          >
            Save
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>
